/**
 * EAN validation utilities
 * Pure functions with no side effects - suitable for unit testing
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates that an EAN has at least 3 characters (after trimming)
 * @param ean - The EAN string to validate
 * @returns Validation result with error message if invalid
 */
export function validateEanLength(ean: string): ValidationResult {
  if (!ean) {
    return { valid: true }; // Empty EAN is allowed (no new EAN to add)
  }

  const trimmedEan = ean.trim();
  if (trimmedEan.length > 0 && trimmedEan.length < 3) {
    return {
      valid: false,
      error: 'EAN musí mít alespoň 3 znaky',
    };
  }

  return { valid: true };
}

/**
 * Validates that an EAN contains only alphanumeric characters
 * @param ean - The EAN string to validate
 * @returns Validation result with error message if invalid
 */
export function validateEanAlphanumeric(ean: string): ValidationResult {
  if (!ean || ean.trim().length === 0) {
    return { valid: true }; // Empty EAN is allowed
  }

  const trimmedEan = ean.trim();
  if (!/^[a-zA-Z0-9/-]+$/.test(trimmedEan)) {
    return {
      valid: false,
      error: 'EAN může obsahovat pouze písmena a čísla',
    };
  }

  return { valid: true };
}

/**
 * Performs all EAN validations
 * @param ean - The EAN string to validate
 * @returns Validation result with all error messages
 */
export function validateEan(ean: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const lengthResult = validateEanLength(ean);
  if (!lengthResult.valid && lengthResult.error) {
    errors.push(lengthResult.error);
  }

  const alphanumericResult = validateEanAlphanumeric(ean);
  if (!alphanumericResult.valid && alphanumericResult.error) {
    errors.push(alphanumericResult.error);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
